import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, User, BookOpen, Sparkles, Mail, Phone, MapPin, CreditCard, Contact, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/page-header';
import { RecordLink } from '@/components/records/RecordLink';
import { TabBar } from '@/components/ui/tab-bar';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { CustomerLedgerPage } from './CustomerLedgerPage';

interface LoyaltyTier { name: string; minPoints: number; multiplier?: number }
interface LoyaltyTxRow {
  id: string;
  type: string;
  points: number;
  balanceBefore: number;
  balanceAfter: number;
  notes?: string | null;
  createdAt: string;
  sale?: { id?: string; invoiceNumber: string } | null;
}
interface LoyaltyHistory {
  customer: {
    loyaltyPoints: number;
    loyaltyPointsEarned: number;
    cashValue: number;
    currentTier?: LoyaltyTier | string | null;
    nextTier?: LoyaltyTier | null;
    pointsToNextTier?: number | null;
  };
  transactions: LoyaltyTxRow[];
  total: number;
}

const LOYALTY_PAGE_SIZE = 10;
const LOYALTY_TYPE_LABEL: Record<string, string> = {
  EARN: 'Earned', REDEEM: 'Redeemed', ADJUST: 'Adjusted', EXPIRE: 'Expired', RETURN: 'Returned',
};

function tierName(t?: LoyaltyTier | string | null): string | null {
  if (!t) return null;
  return typeof t === 'string' ? t : t.name;
}

/** POS-I130: points balance, what they are worth, tier progress and the earn/redeem/adjust history. */
function LoyaltyPanel({ customerId, fallbackPoints }: { customerId: string; fallbackPoints: number }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useQuery<LoyaltyHistory>({
    queryKey: ['customer-loyalty-history', customerId, page],
    queryFn: () => api.get(`/loyalty/customers/${customerId}/history`, { params: { page, limit: LOYALTY_PAGE_SIZE } }).then((r) => r.data),
  });
  const c = data?.customer;
  const points = c?.loyaltyPoints ?? fallbackPoints;
  const current = tierName(c?.currentTier);
  const rows = data?.transactions ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Loyalty</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-warning to-warning/80 text-warning-foreground">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <p className="text-2xl font-bold">{points}</p>
                <p className="text-xs text-muted-foreground">points available</p>
              </div>
            </div>
            {c && (
              <>
                <div>
                  <p className="text-lg font-semibold">{formatCurrency(c.cashValue)}</p>
                  <p className="text-xs text-muted-foreground">value of available points</p>
                </div>
                <div>
                  <p className="text-lg font-semibold">{c.loyaltyPointsEarned}</p>
                  <p className="text-xs text-muted-foreground">points earned in total</p>
                </div>
                {current && (
                  <div>
                    <p className="text-lg font-semibold">{current}</p>
                    <p className="text-xs text-muted-foreground">
                      {c.nextTier && c.pointsToNextTier != null
                        ? `${c.pointsToNextTier} more points to ${c.nextTier.name}`
                        : 'highest tier reached'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Points history</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={5} cols={5} />
          ) : isError ? (
            <p className="text-sm text-muted-foreground">Could not load the points history.</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No points earned or spent yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Invoice / note</Th>
                    <Th className="text-end">Points</Th>
                    <Th className="text-end">Balance</Th>
                  </Tr>
                </THead>
                <TBody>
                  {rows.map((r) => (
                    <Tr key={r.id}>
                      <Td className="text-xs text-muted-foreground" label="Date">{new Date(r.createdAt).toLocaleString()}</Td>
                      <Td label="Type">{LOYALTY_TYPE_LABEL[r.type] ?? r.type}</Td>
                      <Td className="text-xs" label="Invoice / note">
                        {r.sale?.invoiceNumber ? (
                          <RecordLink type="invoice" id={r.sale.id} label={r.sale.invoiceNumber} variant="mono" className="text-xs" />
                        ) : null}
                        {r.notes ? <span className="block text-muted-foreground">{r.notes}</span> : null}
                        {!r.sale?.invoiceNumber && !r.notes ? '—' : null}
                      </Td>
                      <Td className={cn('text-end font-medium', r.points < 0 ? 'text-destructive-text' : 'text-success-text')} label="Points">
                        {r.points > 0 ? `+${r.points}` : r.points}
                      </Td>
                      <Td className="text-end text-xs text-muted-foreground" label="Balance">{r.balanceBefore} → {r.balanceAfter}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
          {(data?.total ?? 0) > LOYALTY_PAGE_SIZE && (
            <PaginationBar page={page} pageSize={LOYALTY_PAGE_SIZE} total={data?.total ?? 0} onPageChange={setPage} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface CustomerProfile {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  ntn?: string | null;
  cnic?: string | null;
  creditLimit?: number | null;
  outstandingBalance?: number | null;
  loyaltyPoints?: number | null;
  notes?: string | null;
  isOnAtl?: boolean | null;
  tags?: string[] | string | null;
  referralCode?: string | null;
  sales?: RecentSale[];
  lifetime?: { totalSpent: number; salesCount: number; lastPurchaseAt: string | null };
}

interface RecentSale {
  id: string;
  invoiceNumber: string;
  status: string;
  totalAmount: number | string;
  createdAt: string;
}

function parseTags(tags: CustomerProfile['tags']): string[] {
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') {
    try { const v = JSON.parse(tags || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
  }
  return [];
}

type Tab = 'overview' | 'ledger' | 'loyalty';

/**
 * Customer 360 — a single detail page that consolidates the customer profile,
 * Khata/Udhar ledger, and loyalty into tabs (replaces the standalone
 * per-customer ledger screen). Reached via `/customers/:id`.
 */
export function CustomerDetailPage() {
  const { t } = useTranslation('customers');
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canEdit = hasPermission('customers:write') || hasPermission('customers:*');
  const canUseLoyalty = hasPermission('loyalty:read') || hasPermission('loyalty:*');
  const [tab, setTab] = useState<Tab>(location.pathname.endsWith('/ledger') ? 'ledger' : 'overview');

  const { data: customer, isLoading } = useQuery<CustomerProfile>({
    queryKey: ['customer', id],
    queryFn: () => api.get(`/customers/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const tags = parseTags(customer?.tags);
  const recentSales = (customer?.sales ?? []).slice(0, 5);

  const tabs: { key: Tab; label: string; icon: typeof User; show: boolean }[] = [
    { key: 'overview', label: 'Overview', icon: User, show: true },
    { key: 'ledger', label: 'Ledger (Khata)', icon: BookOpen, show: true },
    { key: 'loyalty', label: 'Loyalty', icon: Sparkles, show: canUseLoyalty },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/customers/list" className="hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Customers
        </Link>
        <span>/</span>
        <span className="text-foreground font-medium">{customer?.name ?? 'Customer'}</span>
      </div>

      <PageHeader
        title={customer?.name ?? 'Customer'}
        actions={canEdit && id ? (
          <Button variant="secondary" onClick={() => navigate(`/customers/list?edit=${id}`)}>
            <Pencil className="h-4 w-4 me-2" /> Edit
          </Button>
        ) : undefined}
      />

      <TabBar
        tabs={tabs.filter((tb) => tb.show).map((tb) => ({ key: tb.key, label: tb.label, icon: tb.icon }))}
        activeTab={tab}
        onTabChange={(key) => setTab(key as Tab)}
      />

      {tab === 'overview' && (
        isLoading ? (
          <SkeletonTable rows={4} cols={2} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
              <CardContent className="space-y-2.5 text-sm">
                <ProfileRow icon={Mail} label="Email" value={customer?.email} />
                <ProfileRow icon={Phone} label="Phone" value={customer?.phone} />
                <ProfileRow icon={MapPin} label="Address" value={[customer?.address, customer?.city].filter(Boolean).join(', ') || null} />
                <ProfileRow icon={Contact} label="CNIC" value={customer?.cnic} />
                <ProfileRow icon={Contact} label="NTN" value={customer?.ntn} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Account</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Outstanding Balance</span>
                  <span className={cn('font-bold', Number(customer?.outstandingBalance ?? 0) > 0 ? 'text-destructive-text' : 'text-success-text')}>
                    {formatCurrency(Math.abs(Number(customer?.outstandingBalance ?? 0)))}
                  </span>
                </div>
                {customer?.creditLimit != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><CreditCard className="h-4 w-4" /> Credit Limit</span>
                    <span className="font-medium">{formatCurrency(Number(customer.creditLimit))}</span>
                  </div>
                )}
                {canUseLoyalty && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Loyalty Points</span>
                    <span className="font-medium">{customer?.loyaltyPoints ?? 0}</span>
                  </div>
                )}
                {customer?.lifetime && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Total spent</span>
                      <span className="font-medium">{formatCurrency(customer.lifetime.totalSpent)} <span className="text-xs text-muted-foreground">({t('salesCount', { count: customer.lifetime.salesCount })})</span></span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Last purchase</span>
                      <span className="font-medium">{customer.lifetime.lastPurchaseAt ? new Date(customer.lifetime.lastPurchaseAt).toLocaleDateString() : '—'}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">ATL status</span>
                  <span className={cn('font-medium', customer?.isOnAtl ? 'text-success-text' : 'text-warning-text')}>{customer?.isOnAtl ? 'On ATL' : 'Not on ATL'}</span>
                </div>
                {customer?.referralCode && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Referral code</span>
                    <span className="font-mono font-medium">{customer.referralCode}</span>
                  </div>
                )}
              </CardContent>
            </Card>
            {(tags.length > 0 || customer?.notes) && (
              <Card className="sm:col-span-2">
                <CardHeader><CardTitle className="text-base">Tags and notes</CardTitle></CardHeader>
                <CardContent className="space-y-2.5 text-sm">
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map((tag) => (
                        <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">{tag}</span>
                      ))}
                    </div>
                  )}
                  {customer?.notes && <p className="whitespace-pre-wrap text-muted-foreground">{customer.notes}</p>}
                </CardContent>
              </Card>
            )}
            <Card className="sm:col-span-2">
              <CardHeader><CardTitle className="text-base">Recent sales</CardTitle></CardHeader>
              <CardContent>
                {recentSales.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No sales yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table mobileCards>
                      <THead>
                        <Tr hover={false}>
                          <Th>Date</Th>
                          <Th>Invoice</Th>
                          <Th>Status</Th>
                          <Th className="text-end">Total</Th>
                        </Tr>
                      </THead>
                      <TBody>
                        {recentSales.map((sl) => (
                          <Tr key={sl.id}>
                            <Td className="text-xs text-muted-foreground" label="Date">{new Date(sl.createdAt).toLocaleDateString()}</Td>
                            <Td label="Invoice">
                              <RecordLink type="invoice" id={sl.id} label={sl.invoiceNumber} variant="mono" />
                            </Td>
                            <Td className="text-xs" label="Status">{sl.status}</Td>
                            <Td className="text-end font-medium" label="Total">{formatCurrency(Number(sl.totalAmount))}</Td>
                          </Tr>
                        ))}
                      </TBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )
      )}

      {tab === 'ledger' && <CustomerLedgerPage embedded />}

      {tab === 'loyalty' && id && <LoyaltyPanel customerId={id} fallbackPoints={customer?.loyaltyPoints ?? 0} />}
    </div>
  );
}

function ProfileRow({ icon: Icon, label, value }: { icon: typeof User; label: string; value?: string | null }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="text-muted-foreground w-20 shrink-0">{label}</span>
      <span className="truncate font-medium">{value || '—'}</span>
    </div>
  );
}
